'use client';

// module imports
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';

// file imports
import { useDebouncedSearch } from '@/hooks';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getUsers } from '@/redux/slices/userSlice';
import { IUser } from '@/types';

interface SpeakerSearchPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectSpeaker?: (speaker: IUser) => void;
  currentSpeakers?: string[];
}

export default function SpeakerSearchPopup({ isOpen, onClose, onSelectSpeaker, currentSpeakers }: SpeakerSearchPopupProps) {
  const dispatch = useAppDispatch();
  const { data: users, loading, error } = useAppSelector((state) => state.user);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<IUser[]>([]);
  const [speakingCategory, setSpeakingCategory] = useState('');
  const [priceRange, setPriceRange] = useState('');

  const searchFunction = useCallback(
    (search: string) => {
      if (isOpen) {
        const params: Record<string, string> = {};

        if (search) {
          params.search = search;
        }

        if (speakingCategory) {
          params.speakingCategory = speakingCategory;
        }

        if (priceRange) {
          params.price = priceRange;
        }

        dispatch(getUsers({ params }));
      }
    },
    [isOpen, dispatch, speakingCategory, priceRange]
  );

  const { search: debouncedSearch } = useDebouncedSearch(searchFunction, 300);
  useEffect(() => {
    if (isOpen) {
      debouncedSearch(searchTerm);
    }
  }, [isOpen, searchTerm, debouncedSearch]);

  // Trigger search when filters change
  useEffect(() => {
    if (isOpen) {
      debouncedSearch(searchTerm);
    }
  }, [speakingCategory, priceRange, debouncedSearch, isOpen, searchTerm]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isOpen && !target.closest('.popup-content')) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Prevent background scroll when popup is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (users && Array.isArray(users)) {
      setFilteredUsers(users);
    }
  }, [users]);

  const handleSpeakerSelect = async (speaker: IUser) => {
    try {
      if (currentSpeakers?.includes(speaker._id)) {
        console.log('Speaker is already selected for this event');
        return;
      }

      if (onSelectSpeaker) {
        onSelectSpeaker(speaker);
      }

      // Close the popup
      onClose();
    } catch (error) {
      console.error('Failed to select speaker:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="popup-content relative bg-white rounded-2xl sm:rounded-3xl p-3 sm:p-4 w-full max-w-2xl max-h-[80vh] sm:max-h-[75vh] overflow-hidden flex flex-col shadow-2xl ml-2 sm:ml-16 md:ml-20 z-[10000]">
        {/* Header */}
        <div className="flex justify-between items-center mb-3 sm:mb-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-900">Search for Speakers</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-base sm:text-lg font-bold cursor-pointer">
            ×
          </button>
        </div>

        {/* Search Input */}
        <div className="mb-3 sm:mb-4">
          <input type="text" placeholder="Search by name or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5167b2] focus:border-transparent" />
        </div>

        {/* Speaker Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3 mb-3 sm:mb-4">
          <select value={speakingCategory} onChange={(e) => setSpeakingCategory(e.target.value)} className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5167b2] focus:border-transparent cursor-pointer">
            <option value="">Select Speaking Category</option>
            <option value="Money">Money</option>
            <option value="AI">AI</option>
            <option value="Leadership">Leadership</option>
          </select>
          <select value={priceRange} onChange={(e) => setPriceRange(e.target.value)} className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-[#5167b2] focus:border-transparent cursor-pointer">
            <option value="">Select Speaker Price Range</option>
            <option value="0-1000">$0 - $1,000</option>
            <option value="1000-5000">$1,000 - $5,000</option>
            <option value="5000-10000">$5,000 - $10,000</option>
            <option value="10000+">$10,000+</option>
          </select>
          <button
            onClick={() => {
              setSpeakingCategory('');
              setPriceRange('');
              setSearchTerm('');
            }}
            className="bg-[#5167b2] text-white font-bold px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm rounded-lg sm:rounded-xl shadow hover:bg-[#4158a2] transition cursor-pointer"
          >
            Clear Filters
          </button>
        </div>

        {/* Users List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-3 sm:py-4">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-2 border-gray-300 border-t-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2 text-xs sm:text-sm">Loading speakers...</p>
            </div>
          ) : error ? (
            <div className="text-center py-3 sm:py-4">
              <p className="text-red-500 text-xs sm:text-sm">Error loading speakers: {error}</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-3 sm:py-4">
              <p className="text-gray-500 text-xs sm:text-sm">{searchTerm ? 'No speakers found matching your search.' : 'No speakers available.'}</p>
            </div>
          ) : (
            <div className="grid gap-1.5 sm:gap-2">
              {filteredUsers.map((user) => (
                <div key={user._id} className="border border-gray-200 rounded-lg sm:rounded-xl p-2 sm:p-3 hover:border-[#5167b2] hover:shadow-md transition-all cursor-pointer" onClick={() => handleSpeakerSelect(user)}>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                      <Image src={user.profilePhoto || 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_640.png'} alt={`${user.name || 'Speaker'}`} width={32} height={32} className="w-full h-full object-cover sm:w-10 sm:h-10" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-xs sm:text-sm">{user?.name || 'Speaker'}</h3>
                      <p className="text-gray-600 text-xs">{user.email}</p>
                      {user.role && <span className="inline-block bg-blue-100 text-blue-800 text-xs px-1 sm:px-1.5 py-0.5 rounded-full mt-1">{user.role}</span>}
                    </div>
                    <div className="text-gray-400">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">
              {filteredUsers.length} speaker{filteredUsers.length !== 1 ? 's' : ''} found
            </span>
            <button onClick={onClose} className="px-3 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm text-gray-600 hover:text-gray-800 font-medium cursor-pointer">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
